import { memo } from "react";
import { Globe, Linkedin, Twitter } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { Button } from "./button";
import { cn } from "~/lib/cn";

export interface AuthorData {
  name: string;
  title: string;
  company?: string;
  avatarUrl: string;
  bio?: string;
  linkedInUrl?: string;
  twitterUrl?: string;
  websiteUrl?: string;
}

export interface AuthorCardProps {
  author: AuthorData;
  variant?: "compact" | "medium" | "large";
  showBio?: boolean;
  showSocialLinks?: boolean;
  className?: string;
  titleLevel?: "h1" | "h2" | "h3" | "p";
}

const getInitials = (name: string): string => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();
};

const getSizeClasses = (variant: "compact" | "medium" | "large") => {
  switch (variant) {
    case "compact":
      return {
        container: "gap-3",
        avatar: "h-10 w-10",
        avatarFallback: "",
        name: "text-sm font-medium",
        title: "text-xs",
        bio: "text-xs",
        gap: "space-y-2",
      };
    case "medium":
      return {
        container: "gap-4 md:gap-5",
        avatar: "size-16 md:size-20",
        avatarFallback: "",
        name: "text-sm font-medium md:text-base",
        title: "text-sm md:text-base",
        bio: "text-base",
        gap: "space-y-3 mb-2",
      };
    case "large":
      return {
        container: "gap-6",
        avatar: "size-24 md:size-32",
        avatarFallback: "text-lg",
        name: "text-xl font-semibold",
        title: "text-base",
        bio: "text-sm leading-relaxed",
        gap: "space-y-3",
      };
  }
};

export const AuthorCard = memo(function AuthorCard({
  author,
  variant = "medium",
  showBio = false,
  showSocialLinks = false,
  className,
  titleLevel = "p",
}: AuthorCardProps) {
  const initials = getInitials(author.name);
  const classes = getSizeClasses(variant);

  const hasSocialLinks =
    showSocialLinks &&
    (author.linkedInUrl || author.twitterUrl || author.websiteUrl);

  const NameComponent = titleLevel;
  const nameProps = titleLevel !== "p" ? {} : {};

  return (
    <div className={cn("flex items-center", classes.container, className)}>
      <Avatar className={cn("shrink-0", classes.avatar)}>
        <AvatarImage src={author.avatarUrl} alt={author.name} />
        <AvatarFallback className={classes.avatarFallback}>
          {initials}
        </AvatarFallback>
      </Avatar>

      <div className={cn("min-w-0 flex-1", classes.gap)}>
        <div>
          <NameComponent
            className={cn("text-gray-900", classes.name)}
            {...nameProps}
          >
            {author.name}
          </NameComponent>
          <p className={cn("text-gray-500", classes.title)}>
            {author.title}
            {author.company &&
              (variant === "large"
                ? ` at ${author.company}`
                : `, ${author.company}`)}
          </p>
        </div>

        {hasSocialLinks && (
          <div className="flex gap-2">
            {author.linkedInUrl && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => window.open(author.linkedInUrl, "_blank")}
                aria-label={`${author.name} on LinkedIn`}
              >
                <Linkedin className="h-4 w-4" />
              </Button>
            )}
            {author.twitterUrl && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => window.open(author.twitterUrl, "_blank")}
                aria-label={`${author.name} on Twitter`}
              >
                <Twitter className="h-4 w-4" />
              </Button>
            )}
            {author.websiteUrl && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => window.open(author.websiteUrl, "_blank")}
                aria-label={`${author.name}'s website`}
              >
                <Globe className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}

        {showBio && author.bio && (
          <p className={cn("text-gray-600", classes.bio)}>{author.bio}</p>
        )}
      </div>
    </div>
  );
});
